import { Asset } from "../../types";
import { Badge, BadgeVariants } from "~/components/Badge";
import { ImageLoader } from "~/components/ImageLoader";
import { Icon } from "~/components/Icon";
import { faUserGroup } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    ASSET_CLASS_IDS,
    PROTOCOL_IDS,
    RWA_COIN_ICON,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { Link } from "~/components/Link";
import { getManagerURL, getPlatformURL } from "~/routes";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import {
    getUniqueTokenizationTypeIDs,
    TokenizationTypeBadge,
} from "~/components/TokenizationTypeBadge";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import { BadgeArray } from "~/components/BadgeArray";
import { DescriptionDisplay } from "~/components/DescriptionDisplay";
import { AssetRatingBadge } from "~/modules/assets/components/AssetRatingBadge";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { useIsMobile } from "~/hooks/use-mobile";
import { ClassnamePassthruWrapper } from "~/components/ui/classname-passthru-wrapper";
import { useState } from "react";
import { HowToBuyModal } from "~/modules/assets/components/HowToBuyModal";
import { track } from "~/modules/analytics/track";

// // // //

function ProtocolInfo(props: { asset: Asset }) {
    const protocol = props.asset.tokens[0]?.protocol;

    if (!protocol) return null;

    return (
        <Link
            variant="default"
            className="flex items-center gap-1.5 text-sm whitespace-nowrap"
            href={getPlatformURL({ platformSlug: protocol.slug })}
            analyticsLabel={AssetUserEvents.AssetHeaderPlatformLink}
        >
            <span className="text-xs text-muted-foreground">Offered by</span>
            {protocol.icon_url && (
                <ImageLoader
                    src={protocol.icon_url}
                    alt={`${protocol.name} icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    fallbackClassName="bg-white p-2 opacity-50"
                    className="size-4 min-w-4 rounded-full select-none"
                />
            )}
            <div>{protocol.name}</div>
        </Link>
    );
}

function ManagerInfo(props: { asset: Asset }) {
    const manager = props.asset.manager;

    if (!manager) return null;

    return (
        <Link
            variant="default"
            className="flex min-w-0 max-w-full items-center gap-1.5 text-sm"
            href={getManagerURL({ managerSlug: manager.slug })}
            analyticsLabel={AssetUserEvents.ClickManagerIndividualLink}
            analyticsEventProperties={{ manager: manager.slug }}
        >
            <span className="shrink-0 text-xs whitespace-nowrap text-muted-foreground">
                Managed by
            </span>
            {manager.icon_url && (
                <ImageLoader
                    src={manager.icon_url}
                    alt={`${manager.name} icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    fallbackClassName="bg-white p-2 opacity-50"
                    className="size-4 min-w-4 rounded-full select-none"
                />
            )}
            <div className="truncate">{manager.name}</div>
        </Link>
    );
}

interface HeaderMetadataItem {
    key: string;
    content: React.ReactNode;
    hideOnMobile?: boolean;
}

function AssetHeaderMetadata(props: { asset: Asset }) {
    const { asset } = props;

    const IS_BUIDL = asset.ticker?.toLowerCase() === "buidl";
    const IS_FILQ = asset.ticker?.toLowerCase() === "filq";
    const IS_BENJI = asset.ticker?.toLowerCase() === "benji";

    // Set flags to show asset rating badges
    const SHOW_MOODY_AAA_MF_BADGE = IS_BUIDL || IS_FILQ;
    const SHOW_SP_AAAM_BADGE = IS_BENJI;
    const SHOW_FITCH_AAAMMF_BADGE = IS_BENJI;

    const investorTypes =
        asset.primary_markets?.flatMap((m) => m.allowed_investor_types || []) ||
        [];
    const uniqueInvestorTypes = uniqueValues(investorTypes);

    // Get unique tokenization type IDs, excluding OnChainDistributed
    // NOTE - this excludes tokens where total supply is 0
    const tokenizationTypeIDs = getUniqueTokenizationTypeIDs(asset.tokens);

    // Define a flag to determine whether or not to show the tokenization type badges
    // Hide the badges if there's only a Distributed badge to display
    const hideTokenizationTypeBadges =
        tokenizationTypeIDs.length === 1 &&
        tokenizationTypeIDs[0] === TOKENIZATION_TYPE_IDS.OnChainDistributed;

    const assetClassMetadata = getAssetClassLinkMetadata(asset.asset_class.id);

    // Determine if we're displaying any asset rating badges
    const showRatings =
        SHOW_MOODY_AAA_MF_BADGE ||
        SHOW_SP_AAAM_BADGE ||
        SHOW_FITCH_AAAMMF_BADGE;

    const items: HeaderMetadataItem[] = [
        {
            key: "rating",
            content: showRatings && (
                <ClassnamePassthruWrapper
                    className={cn({
                        // Only wrap in flex container if there are multiple badges
                        "flex flex-wrap items-center gap-x-3":
                            [
                                SHOW_MOODY_AAA_MF_BADGE,
                                SHOW_FITCH_AAAMMF_BADGE,
                                SHOW_SP_AAAM_BADGE,
                            ].filter(Boolean).length > 1,
                    })}
                >
                    {SHOW_MOODY_AAA_MF_BADGE && (
                        <AssetRatingBadge rating="moodys_aaa" />
                    )}
                    {SHOW_FITCH_AAAMMF_BADGE && (
                        <AssetRatingBadge rating="fitch_aaammf" />
                    )}
                    {SHOW_SP_AAAM_BADGE && (
                        <AssetRatingBadge rating="sp_aaam" />
                    )}
                </ClassnamePassthruWrapper>
            ),
        },
        {
            key: "tokenization-types",
            content: hideTokenizationTypeBadges ? null : (
                <div className="flex items-center gap-3">
                    {tokenizationTypeIDs.map((tokenization_type_id) => (
                        <TokenizationTypeBadge
                            size="sm"
                            suffix="Asset"
                            key={tokenization_type_id}
                            tokenization_type_id={tokenization_type_id}
                        />
                    ))}
                </div>
            ),
        },
        {
            key: "asset-class",
            content: assetClassMetadata && (
                <Badge
                    variant={BadgeVariants.gray}
                    className="gap-1.5 whitespace-nowrap cursor-default"
                    size="sm"
                    rounded="md"
                    label={
                        <>
                            <Icon
                                className="h-2.5 opacity-80"
                                icon={assetClassMetadata.icon}
                            />
                            {assetClassMetadata.label}
                        </>
                    }
                />
            ),
        },
        {
            key: "investor-types",
            content: uniqueInvestorTypes.length > 0 && (
                <BadgeArray
                    tooltipTitle="Eligibility"
                    values={uniqueInvestorTypes}
                    className="flex-wrap gap-x-3 gap-y-1.5 sm:gap-3"
                    icon={
                        <FontAwesomeIcon
                            className="h-2.5 opacity-80"
                            icon={faUserGroup}
                        />
                    }
                />
            ),
        },
        {
            key: "protocol",
            content: asset.tokens[0]?.protocol && (
                <ProtocolInfo asset={asset} />
            ),
            hideOnMobile: true,
        },
        {
            key: "manager",
            content: asset.manager && <ManagerInfo asset={asset} />,
        },
    ];

    const visibleItems = items.filter((item) => Boolean(item.content));

    if (visibleItems.length === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:gap-3">
            {visibleItems.map((item) => (
                <div
                    key={item.key}
                    data-asset-header-segment=""
                    className={cn(
                        "flex max-w-full shrink-0 items-center border-l border-border pl-3 first:border-l-0 first:pl-0",
                        {
                            "max-sm:hidden": item.hideOnMobile,
                        },
                    )}
                >
                    {item.content}
                </div>
            ))}
        </div>
    );
}

export function AssetHeader(props: { asset: Asset }) {
    const { asset } = props;
    const isMobile = useIsMobile();
    const [showHowToBuyModal, setShowHowToBuyModal] = useState(false);

    const IS_SECURITIZE_ASSET =
        asset.protocol_ids?.includes(PROTOCOL_IDS.Securitize) || false;

    const IS_ALPHALEDGER = asset.ticker?.toLowerCase() === "t12";

    const IS_REAL_ESTATE = asset.asset_class.id === ASSET_CLASS_IDS.RealEstate;

    const showTicker =
        !IS_SECURITIZE_ASSET && !IS_REAL_ESTATE && !IS_ALPHALEDGER;

    return (
        <div className="flex flex-col gap-4 mb-5">
            <div className="flex flex-col lg:flex-row lg:justify-between mt-6 gap-4">
                {/* Left side */}
                <div className="flex flex-col">
                    <div className="flex flex-row items-center gap-4 sm:gap-5 mb-4">
                        <div className="flex shrink-0 flex-col items-center">
                            <ImageLoader
                                src={asset.icon_url}
                                alt={`${asset.ticker || asset.name} Icon`}
                                fallbackSrc={RWA_COIN_ICON}
                                fallbackClassName="bg-white p-2 shadow"
                                className="size-12 sm:size-20 min-w-12 sm:min-w-20 shrink-0 rounded-full select-none"
                            />
                            {showTicker && asset.ticker && (
                                <p className="mt-1 font-mono text-xs font-medium text-muted-foreground sm:hidden">
                                    ${asset.ticker}
                                </p>
                            )}
                        </div>

                        <div className="flex min-w-0 flex-col gap-0 sm:gap-1">
                            {asset.tokens[0]?.protocol && (
                                <div className="sm:hidden">
                                    <ProtocolInfo asset={asset} />
                                </div>
                            )}
                            {showTicker && asset.ticker && (
                                <p className="font-mono text-sm text-muted-foreground max-sm:hidden">
                                    ${asset.ticker}
                                </p>
                            )}
                            <h1 className="text-[1.625rem]/[1.1] font-medium tracking-tight text-balance wrap-break-word text-foreground sm:text-4xl">
                                {asset.name}
                            </h1>
                        </div>
                    </div>

                    <DescriptionDisplay
                        description={asset.description}
                        className="mt-0 mb-3"
                        textClassName={cn("text-pretty", {
                            "text-[0.8125rem]": isMobile,
                        })}
                        toggleVariant={isMobile ? "pill" : "inline"}
                    />

                    <AssetHeaderMetadata asset={asset} />
                </div>

                {/* Right side */}
                {asset.website && (
                    <div className="w-full lg:w-60">
                        <div className="border p-4 rounded-md bg-card gap-2 flex flex-col select-none">
                            <Button
                                type="button"
                                size="sm"
                                variant="primary"
                                className="w-full cursor-pointer"
                                onClick={() => {
                                    setShowHowToBuyModal(true);
                                    track(
                                        AssetUserEvents.AssetHeaderOpenHowToBuy,
                                        {
                                            asset_id: asset.id,
                                            asset_name: asset.name,
                                        },
                                    );
                                }}
                            >
                                How to Buy
                            </Button>
                            <div className="text-xs font-light text-slate-600 text-center">
                                Explore issuer purchase options or request
                                access through RWA.xyz.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {asset.website && (
                <HowToBuyModal
                    assetID={asset.id}
                    assetName={asset.name}
                    assetIconUrl={asset.icon_url}
                    platformName={asset.tokens[0]?.protocol?.name}
                    issuerName={asset.issuer?.name}
                    website={asset.website}
                    show={showHowToBuyModal}
                    onHide={() => setShowHowToBuyModal(false)}
                />
            )}
        </div>
    );
}
